import { Box, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LoadingComp } from "../../../shared/components/loading/Loading";
import { LayoutBaseDePagina } from "../../../shared/layouts";
import { resolveDatesFromData } from "../../sinister/helpers/resolveDatesFromData";
import { ToolbarSinisterGeneric } from "../../sinister/components/ToolbarSinisterGeneric/ToolbarSinisterGeneric";
import { useCollectionControlClientOptions } from "../hooks/useCollectionControlClientOptions";
import { useCollectionControlInstallments } from "../hooks/useCollectionControlInstallments";
import { fieldsConfig } from "./fieldsConfig";
import { TableCollectionControl } from "./TableCollectionControl";

// Consome GET /sinister-agreements/installments (contrato confirmado, ver
// PR-10-controle-cobranca-listagem-parcelas-frontend.md e MANUAL-BACKEND-ENDPOINTS-FRONTEND.md) — filtro
// e paginação acontecem no backend; a toolbar de pesquisa segue o mesmo padrão (ToolbarSinisterGeneric +
// fieldsConfig) já usado em Sinistros/Cobrança/Acordos.
export const CollectionControlListPresentation = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const clientNames = useCollectionControlClientOptions();

	const clientNameOptions = useMemo(
		() => [{ value: "Todos", label: "Todos" }, ...clientNames.map((name) => ({ value: name, label: name }))],
		[clientNames]
	);

	const dynamicFieldsConfig = useMemo(() => {
		return fieldsConfig.map((field) => (field.key === "clientName" ? { ...field, options: clientNameOptions } : field));
	}, [clientNameOptions]);

	const data = searchParams.get("data") || "7";
	const isPeriodo = data === "periodo";
	const resolvedDates = isPeriodo
		? { dataIni: searchParams.get("dataIni") || "", dataFim: searchParams.get("dataFim") || "" }
		: resolveDatesFromData(data);

	const filtersFromSearchParams = {
		clientName: searchParams.get("clientName") || "Todos",
		installmentStatus: searchParams.get("installmentStatus") || "Todos",
		agreementStatus: (searchParams.get("agreementStatus") || "Todos").split(","),
		paymentMethod: searchParams.get("paymentMethod") || "Todos",
		filterField: (searchParams.get("filterField") || "documento") as "documento" | "nome" | "certificado",
		textSearch: searchParams.get("textSearch") || "",
		dateType: (searchParams.get("dateType") || "vencimento") as "vencimento" | "pagamento",
		data,
		dataIni: resolvedDates.dataIni,
		dataFim: resolvedDates.dataFim,
	};

	const { rows, totalCount, isLoading, error, refetch } = useCollectionControlInstallments(page, rowsPerPage, filtersFromSearchParams);

	const handleSearch = (filters: Record<string, any>) => {
		const params = new URLSearchParams();
		params.set("clientName", filters.clientName || "Todos");
		params.set("installmentStatus", filters.installmentStatus || "Todos");
		params.set("agreementStatus", Array.isArray(filters.agreementStatus) ? filters.agreementStatus.join(",") : filters.agreementStatus || "Todos");
		params.set("paymentMethod", filters.paymentMethod || "Todos");
		params.set("filterField", filters.filterField || "documento");
		params.set("textSearch", filters.textSearch || "");
		params.set("dateType", filters.dateType || "vencimento");
		params.set("data", filters.data || "7");
		if (filters.data === "periodo") {
			params.set("dataIni", filters.dataIni || "");
			params.set("dataFim", filters.dataFim || "");
		}
		setSearchParams(params, { replace: true });
		setPage(0);
	};

	const handleClearFilters = () => {
		setSearchParams({}, { replace: true });
		setPage(0);
	};

	return (
		<>
			<LayoutBaseDePagina
				titulo="Controle de Cobranças"
				subtitulo="Listagem consolidada de todas as parcelas de acordos em cobrança"
				barraDeFerramentas={
					<ToolbarSinisterGeneric
						fieldsConfig={dynamicFieldsConfig}
						filterInitialParams={filtersFromSearchParams}
						onSearch={handleSearch}
						onClear={handleClearFilters}
					/>
				}
			>
				{error ? (
					<Box px={2}>
						<Typography variant="body2" color="error">
							{error}
						</Typography>
					</Box>
				) : (
					<Stack>
						<Box px={2} pt={1}>
							<Typography variant="body2" color="text.secondary">
								{totalCount} parcela{totalCount === 1 ? "" : "s"} encontrada{totalCount === 1 ? "" : "s"}.
							</Typography>
						</Box>
						<TableCollectionControl
							rows={rows}
							totalCount={totalCount}
							page={page}
							rowsPerPage={rowsPerPage}
							onPageChange={setPage}
							onRowsPerPageChange={(newLimit) => {
								setRowsPerPage(newLimit);
								setPage(0);
							}}
							onChanged={refetch}
						/>
					</Stack>
				)}
			</LayoutBaseDePagina>
			<LoadingComp isopen={isLoading} />
		</>
	);
};
